import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Category {
  nameKey: string;
  url: string;
  icon: string;
}

const categories: Category[] = [
  { nameKey: 'category_shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
  { nameKey: 'category_hoodies', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', icon: 'ri-t-shirt-line' },
  { nameKey: 'category_tshirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', icon: 'ri-t-shirt-air-line' },
  { nameKey: 'category_jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', icon: 'ri-honour-line' },
  { nameKey: 'category_pants', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html', icon: 'ri-pantone-line' },
  { nameKey: 'category_headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html', icon: 'ri-ghost-line' },
  { nameKey: 'category_sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html', icon: 'ri-stack-line' },
  { nameKey: 'category_underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html', icon: 'ri-checkbox-blank-circle-line' },
  { nameKey: 'category_jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html', icon: 'ri-shirt-line' },
  { nameKey: 'category_accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', icon: 'ri-glasses-line' },
  { nameKey: 'category_bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', icon: 'ri-handbag-line' },
  { nameKey: 'category_belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html', icon: 'ri-link' },
  { nameKey: 'category_perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html', icon: 'ri-drop-line' },
  { nameKey: 'category_other', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html', icon: 'ri-more-line' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handlePointerOutside(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('pointerdown', handlePointerOutside);
    }
    return () => document.removeEventListener('pointerdown', handlePointerOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <nav className="relative w-full bg-background-50/80 backdrop-blur-sm border-b border-background-200/70 sticky top-0 z-50" ref={menuRef}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        <Link
          to="/"
          onClick={() => setMenuOpen(false)}
          className="font-heading text-base font-bold text-foreground-950 cursor-pointer whitespace-nowrap"
        >
          Kakobuy<span className="text-accent-500">Spreadsheet</span>
        </Link>

        <div className="hidden md:flex items-center gap-5">
          <Link to="/" className="whitespace-nowrap cursor-pointer text-sm text-foreground-700 hover:text-foreground-950 transition-colors">
            <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            {t('nav_home')}
          </Link>
          <Link to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik" className="whitespace-nowrap cursor-pointer text-sm text-foreground-700 hover:text-foreground-950 transition-colors">
            <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            {t('nav_guide')}
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap cursor-pointer text-sm text-foreground-700 hover:text-foreground-950 transition-colors"
          >
            Kakobuy Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap cursor-pointer bg-primary-500 hover:bg-primary-600 text-white font-semibold px-4 py-1.5 rounded-full text-sm transition-all duration-200"
          >
            {t('cta_buy_now')}
          </a>
        </div>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          onPointerDown={(e) => e.stopPropagation()}
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-800"
          aria-label="Menu"
        >
          {menuOpen ? (
            <i className="ri-close-line text-2xl"></i>
          ) : (
            <i className="ri-menu-line text-2xl"></i>
          )}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden absolute inset-x-0 top-full h-[calc(100dvh-3.5rem)] bg-background-50 overflow-y-auto z-50 border-t border-background-200/70">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100 cursor-pointer transition-colors"
            >
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
              {t('nav_home')}
            </Link>
            <Link
              to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100 cursor-pointer transition-colors"
            >
              <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
              {t('nav_guide')}
            </Link>

            <div className="border-t border-background-200/70 my-3"></div>

            <button
              onClick={() => setCatOpen(!catOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-semibold text-foreground-900 hover:bg-background-100 cursor-pointer transition-colors"
            >
              <span className="flex items-center gap-3">
                <i className="ri-apps-2-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                {t('categories_title')}
              </span>
              {catOpen ? (
                <i className="ri-arrow-up-s-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              ) : (
                <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              )}
            </button>

            {catOpen && (
              <div className="pl-4 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-accent-500`}></i>
                    <span>{t(cat.nameKey)}</span>
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/70 my-3"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-900 hover:bg-background-100 cursor-pointer transition-colors"
            >
              <i className="ri-external-link-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
              Kakobuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 mx-4 mt-2 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm text-center justify-center cursor-pointer transition-all duration-200"
            >
              <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center"></i>
              {t('cta_buy_now')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}